import { cleanPermalink } from './top-videos';
import { format } from 'date-fns';

import { STATS_COLUMNS } from '../types/statistics';
import type { MediaStats } from '../types/statistics';
import { getAccessToken, staleGoogleTokenError } from './google-auth';
import type { ReportPage } from './export-docs';

export const SHEETS_URL = 'https://docs.google.com/spreadsheets/create';
export const SHEETS_OPEN_DELAY_MS = 1500;
export const SHEETS_HINT_DURATION_MS = 6000;

function buildTsv(data: MediaStats[]): string {
  // Drop the Instagram/Facebook impression columns when every row is TikTok —
  // they'd be meaningless zeros (matches the on-screen table).
  const allTikTok = data.length > 0 && data.every((r) => /tiktok\.com/i.test(r.url));
  const columns = allTikTok
    ? STATS_COLUMNS.filter((c) => c.key !== 'impressionsInstagram' && c.key !== 'impressionsFacebook')
    : STATS_COLUMNS;
  const headers = columns.map(col => col.label);
  const rows = data.map(row => columns.map(col => String(row[col.key] ?? '')));
  return [headers, ...rows].map(row => row.join('\t')).join('\n');
}

export async function copyStatsToClipboard(data: MediaStats[]): Promise<void> {
  const tsv = buildTsv(data);
  await navigator.clipboard.writeText(tsv);
}

const SHEETS_API = 'https://sheets.googleapis.com/v4/spreadsheets';
const SHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets';

const TOTAL_COLORS: Record<number, { red: number; green: number; blue: number }> = {
  0: { red: 1,     green: 1,     blue: 1     }, // #FFFFFF white
  1: { red: 1,     green: 0.949, blue: 0.8   }, // #FFF2CC light yellow
  2: { red: 0.812, green: 0.886, blue: 0.953 }, // #CFE2F3 light blue
  3: { red: 0.988, green: 0.898, blue: 0.804 }, // #FCE5CD orange
  4: { red: 0.957, green: 0.8,   blue: 0.8   }, // #F4CCCC pink
};

type RowData = {
  date: string;
  dateSpan: number; // >1 means merge this many rows in column A; 0 means this row is inside a merged group
  account: string;
  url: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  avgViews: number | null;
  avgLikes: number | null;
  avgComments: number | null;
  avgShares: number | null;
  scoreViews: number;
  scoreLikes: number;
  scoreComments: number;
  scoreShares: number;
  total: number;
};

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function buildRows(pages: ReportPage[]): RowData[] {
  const allPosts = pages.flatMap(p => p.posts);

  const byAccount = new Map<string, MediaStats[]>();
  for (const post of allPosts) {
    const group = byAccount.get(post.accountUsername) ?? [];
    group.push(post);
    byAccount.set(post.accountUsername, group);
  }

  const rows: RowData[] = [];

  for (const posts of byAccount.values()) {
    const avgViews    = Math.round(mean(posts.map(p => p.impressionsTotal)));
    const avgLikes    = Math.round(mean(posts.map(p => p.likes)));
    const avgComments = Math.round(mean(posts.map(p => p.comments)));
    const avgShares   = Math.round(mean(posts.map(p => p.shares)));

    const sorted = [...posts].sort((a, b) => {
      if (!a.publishedAt && !b.publishedAt) return 0;
      if (!a.publishedAt) return 1;
      if (!b.publishedAt) return -1;
      return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime();
    });

    // Group sorted posts by date to compute dateSpan for merging
    const dateGroups: { dateStr: string; posts: MediaStats[] }[] = [];
    for (const post of sorted) {
      const dateStr = post.publishedAt
        ? format(new Date(post.publishedAt), 'dd.MM.yyyy')
        : '';
      const last = dateGroups[dateGroups.length - 1];
      if (last && last.dateStr === dateStr) {
        last.posts.push(post);
      } else {
        dateGroups.push({ dateStr, posts: [post] });
      }
    }

    let isFirstInAccount = true;

    for (const { dateStr, posts: datePosts } of dateGroups) {
      for (let di = 0; di < datePosts.length; di++) {
        const post = datePosts[di];
        const scoreViews    = post.impressionsTotal > avgViews    ? 1 : 0;
        const scoreLikes    = post.likes            > avgLikes    ? 1 : 0;
        const scoreComments = post.comments         > avgComments ? 1 : 0;
        const scoreShares   = post.shares           > avgShares   ? 1 : 0;
        const total         = scoreViews + scoreLikes + scoreComments + scoreShares;

        rows.push({
          date:        di === 0 ? dateStr : '',
          dateSpan:    di === 0 ? datePosts.length : 0,
          account:     post.accountUsername,
          url:         cleanPermalink(post.url),
          views:       post.impressionsTotal,
          likes:       post.likes,
          comments:    post.comments,
          shares:      post.shares,
          avgViews:    isFirstInAccount ? avgViews    : null,
          avgLikes:    isFirstInAccount ? avgLikes    : null,
          avgComments: isFirstInAccount ? avgComments : null,
          avgShares:   isFirstInAccount ? avgShares   : null,
          scoreViews,
          scoreLikes,
          scoreComments,
          scoreShares,
          total,
        });

        isFirstInAccount = false;
      }
    }
  }

  return rows;
}

async function createSpreadsheet(token: string): Promise<string> {
  const res = await fetch(SHEETS_API, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      properties: { title: 'Статистика по публикациям' },
      sheets: [{ properties: { title: 'Sheet1', sheetId: 0 } }],
    }),
  });
  if (!res.ok) throw staleGoogleTokenError(res.status) ?? new Error(`Ошибка создания таблицы: ${res.status}`);
  const data = await res.json();
  return data.spreadsheetId as string;
}

async function writeValues(token: string, id: string, rows: RowData[]): Promise<void> {
  const header = [
    'Дата', 'Аккаунт', 'Ссылка на публикацию',
    'Просмотры', 'Лайки', 'Комментарии', 'Репосты',
    'Среднее значение', '', '', '',
    'Балл прос', 'Балл лайк', 'Балл коммент', 'Балл реп', 'ИТОГО',
  ];

  const dataRows = rows.map(r => [
    r.date, r.account, r.url,
    r.views, r.likes, r.comments, r.shares,
    r.avgViews ?? '', r.avgLikes ?? '', r.avgComments ?? '', r.avgShares ?? '',
    r.scoreViews, r.scoreLikes, r.scoreComments, r.scoreShares, r.total,
  ]);

  const range = `Sheet1!A1:P${1 + rows.length}`;
  const res = await fetch(
    `${SHEETS_API}/${id}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: [header, ...dataRows] }),
    },
  );
  if (!res.ok) throw staleGoogleTokenError(res.status) ?? new Error(`Ошибка записи данных: ${res.status}`);
}

async function applyFormatting(token: string, id: string, rows: RowData[]): Promise<void> {
  const sheetId = 0;
  const requests: object[] = [];

  // Header: Дата–Репосты (A-G) + Балл прос–ИТОГО (L-P): светло-зелёный 1 #D9EAD3
  const lightGreen1 = { red: 0.851, green: 0.918, blue: 0.827 };
  // Header: Среднее значение (H-K): светло-оранжевый 2 #FCE5CD
  const lightOrange2 = { red: 0.988, green: 0.898, blue: 0.804 };

  for (const { startColumnIndex, endColumnIndex, bg } of [
    { startColumnIndex: 0,  endColumnIndex: 7,  bg: lightGreen1  }, // A–G
    { startColumnIndex: 7,  endColumnIndex: 11, bg: lightOrange2 }, // H–K
    { startColumnIndex: 11, endColumnIndex: 16, bg: lightGreen1  }, // L–P
  ]) {
    requests.push({
      repeatCell: {
        range: { sheetId, startRowIndex: 0, endRowIndex: 1, startColumnIndex, endColumnIndex },
        cell: {
          userEnteredFormat: {
            backgroundColor: bg,
            textFormat: { bold: true },
          },
        },
        fields: 'userEnteredFormat(backgroundColor,textFormat)',
      },
    });
  }

  // Merge H1:K1 for "Среднее значение"
  requests.push({
    mergeCells: {
      range: { sheetId, startRowIndex: 0, endRowIndex: 1, startColumnIndex: 7, endColumnIndex: 11 },
      mergeType: 'MERGE_ALL',
    },
  });

  // Column P: background color by ИТОГО value
  for (let i = 0; i < rows.length; i++) {
    const color = TOTAL_COLORS[rows[i].total] ?? TOTAL_COLORS[0];
    requests.push({
      repeatCell: {
        range: { sheetId, startRowIndex: i + 1, endRowIndex: i + 2, startColumnIndex: 15, endColumnIndex: 16 },
        cell: { userEnteredFormat: { backgroundColor: color } },
        fields: 'userEnteredFormat(backgroundColor)',
      },
    });
  }

  // Merge date cells vertically for multi-publication days (column A)
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].dateSpan > 1) {
      requests.push({
        mergeCells: {
          range: {
            sheetId,
            startRowIndex: i + 1,
            endRowIndex:   i + 1 + rows[i].dateSpan,
            startColumnIndex: 0,
            endColumnIndex:   1,
          },
          mergeType: 'MERGE_ALL',
        },
      });
    }
  }

  // Column widths (pixels): A B C   D   E  F   G  H  I  J  K  L  M  N  O  P
  const widths =           [90,100,300,100, 70,100, 80,90,80,90,80,80,80,90,75,70];
  for (let i = 0; i < widths.length; i++) {
    requests.push({
      updateDimensionProperties: {
        range: { sheetId, dimension: 'COLUMNS', startIndex: i, endIndex: i + 1 },
        properties: { pixelSize: widths[i] },
        fields: 'pixelSize',
      },
    });
  }

  // Column C: hyperlinks
  for (let i = 0; i < rows.length; i++) {
    const url = rows[i].url;
    requests.push({
      updateCells: {
        range: { sheetId, startRowIndex: i + 1, endRowIndex: i + 2, startColumnIndex: 2, endColumnIndex: 3 },
        rows: [{
          values: [{
            userEnteredValue: { stringValue: url },
            userEnteredFormat: {
              textFormat: {
                link: { uri: url },
                foregroundColor: { red: 0.067, green: 0.408, blue: 0.714 },
                underline: true,
              },
            },
          }],
        }],
        fields: 'userEnteredValue,userEnteredFormat(textFormat)',
      },
    });
  }

  const res = await fetch(`${SHEETS_API}/${id}:batchUpdate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ requests }),
  });
  if (!res.ok) throw staleGoogleTokenError(res.status) ?? new Error(`Ошибка форматирования: ${res.status}`);
}

export async function generateGoogleSheetsReport(pages: ReportPage[]): Promise<void> {
  const allPosts = pages.flatMap(p => p.posts);
  if (allPosts.length === 0) throw new Error('Нет данных для экспорта');

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('VITE_GOOGLE_CLIENT_ID не задан в .env');

  const token = await getAccessToken(SHEETS_SCOPE);
  const id = await createSpreadsheet(token);
  const rows = buildRows(pages);
  await writeValues(token, id, rows);
  await applyFormatting(token, id, rows);

  window.open(`https://docs.google.com/spreadsheets/d/${id}/edit`, '_blank');
}
